import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createPaymentMethod,
  deletePaymentMethod,
  listPaymentMethods,
  updatePaymentMethod,
} from '@/api/payment-methods';
import { queryKeys } from '@/lib/query-keys';
import type { CreatePaymentMethodRequest, UpdatePaymentMethodRequest } from '@/types/settings';

/**
 * Métodos de pago del negocio activo (módulo 2).
 *
 * El listado lo puede leer cualquier miembro; crear/editar/borrar es solo ADMIN (la pantalla
 * esconde los botones y el backend responde 403 igual si alguien los invoca).
 *
 * Las mutaciones invalidan la lista en vez de escribir en la caché: el backend no devuelve un
 * listado nuevo y hay reglas de negocio (métodos activos, orden por creación) que conviene
 * volver a leer.
 */

/** `GET /api/tenants/:tenantId/payment-methods`. */
export function usePaymentMethods(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.paymentMethods(tenantId ?? ''),
    queryFn: () => listPaymentMethods(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

function invalidatePaymentMethods(queryClient: QueryClient, tenantId: string | null): void {
  if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods(tenantId) });
}

/** `POST /api/tenants/:tenantId/payment-methods` (solo ADMIN). */
export function useCreatePaymentMethod(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePaymentMethodRequest) =>
      createPaymentMethod(tenantId ?? '', payload),
    onSuccess: () => invalidatePaymentMethods(queryClient, tenantId),
  });
}

/** `PATCH .../payment-methods/:paymentMethodId` (solo ADMIN). También se usa para activar/desactivar. */
export function useUpdatePaymentMethod(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      paymentMethodId,
      payload,
    }: {
      paymentMethodId: string;
      payload: UpdatePaymentMethodRequest;
    }) => updatePaymentMethod(tenantId ?? '', paymentMethodId, payload),
    onSuccess: () => invalidatePaymentMethods(queryClient, tenantId),
  });
}

/** `DELETE .../payment-methods/:paymentMethodId` (solo ADMIN). */
export function useDeletePaymentMethod(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) => deletePaymentMethod(tenantId ?? '', paymentMethodId),
    onSuccess: () => invalidatePaymentMethods(queryClient, tenantId),
  });
}
